import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Box, Button, Stack, TextField, Typography } from '@mui/material'
import { BANDS_QUERY_KEY, maiuscula, setJobFunction, suggestFunction, type JobBand, type JobFunction } from '../api/bands'
import { ORG_CHART_QUERY_KEY } from '../api/orgChart'
import { t } from '../i18n'

/**
 * s62 / C3b-2 (F2, ADR-0032) -- FUNCAO DO CARGO, gravada no cargo: na ficha e na coluna da lista de cargos.
 * Mostra a funcao gravada e a faixa; sem funcao, a SUGERIDA pelo titulo (com "Usar sugestao"). Quem altera cargos
 * escolhe na lista (agrupada por faixa); ao gravar, lista, organograma e ficha releem (o cargo muda de linha).
 */
export function JobFunctionField({
  jobId,
  jobName,
  bands,
  atual,
  pode,
  compacto = false,
}: {
  jobId: string
  jobName: string
  bands: readonly JobBand[]
  atual: JobFunction | undefined
  pode: boolean
  compacto?: boolean
}) {
  const qc = useQueryClient()
  const todas = bands.flatMap((b) => b.functions)
  const sugestao = atual ? null : suggestFunction(jobName, todas)
  const faixa = (f: JobFunction | null | undefined) => (f ? (bands.find((b) => b.id === f.bandId)?.name ?? '') : '')
  const m = useMutation({
    mutationFn: (fid: string | null) => setJobFunction(jobId, fid),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: BANDS_QUERY_KEY })
      void qc.invalidateQueries({ queryKey: ORG_CHART_QUERY_KEY })
    },
  })

  const texto = atual ? (
    <Typography variant="body2" component="span" data-testid={`funcao-cargo-${jobId}`}>
      {maiuscula(atual.name)}
      {!compacto && (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {' · '}
          {faixa(atual)}
        </Box>
      )}
    </Typography>
  ) : (
    <Typography variant="body2" component="span" color="text.secondary" sx={{ fontStyle: 'italic' }} data-testid={`funcao-cargo-${jobId}`}>
      {sugestao ? t.jobFunction.suggested(maiuscula(sugestao.name)) : t.jobFunction.none}
    </Typography>
  )

  if (!pode) return texto
  return (
    <Stack
      direction={compacto ? 'row' : { xs: 'column', sm: 'row' }}
      spacing={0.75}
      alignItems={compacto ? 'center' : { sm: 'center' }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <TextField
        select
        size="small"
        value={atual?.id ?? ''}
        disabled={m.isPending}
        onChange={(e) => m.mutate(e.target.value === '' ? null : e.target.value)}
        slotProps={{ select: { native: true }, htmlInput: { 'aria-label': t.jobFunction.choose(jobName) } }}
        sx={{ minWidth: compacto ? 150 : 240 }}
      >
        <option value="">{sugestao ? t.jobFunction.suggested(maiuscula(sugestao.name)) : t.jobFunction.none}</option>
        {bands.map((b) => (
          <optgroup key={b.id} label={b.name}>
            {b.functions.map((f) => (
              <option key={f.id} value={f.id}>
                {maiuscula(f.name)}
              </option>
            ))}
          </optgroup>
        ))}
      </TextField>
      {sugestao && (
        <Button size="small" disabled={m.isPending} onClick={() => m.mutate(sugestao.id)} aria-label={t.jobFunction.useSuggestionFor(jobName, sugestao.name)}>
          {t.jobFunction.useSuggestion}
        </Button>
      )}
      {!compacto && atual && (
        <Typography variant="caption" color="text.secondary">
          {t.jobFunction.band(faixa(atual))}
        </Typography>
      )}
      {m.isError && (
        <Typography variant="caption" color="error">
          {t.jobFunction.error}
        </Typography>
      )}
    </Stack>
  )
}
